import React, { useState, useEffect } from 'react';
import styles from '../styles/TripAdvisorButton.module.css';

interface Review {
  user?: {
   username?: string;
  };
  title?: string;
  text: string;
  rating: number;
}

interface TripAdvisorButtonProps {
  locationId: string;
}

const getReviewsFromResponse = (payload: any): Review[] => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.reviews)) return payload.reviews;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  return [];
};

const getRatingLabel = (rating: number) => {
  if (rating >= 4.5) return "Excellent";
  if (rating >= 3.5) return "Very good";
  if (rating >= 2.5) return "Average";
  if (rating >= 1.5) return "Poor";
  return "Terrible";
};

const getReviewerName = (review?: Review) =>
   review?.user?.username?.trim() || "Anonymous";

// Official TripAdvisor Logo
const TripAdvisorLogo = () => (
  <img 
    src="https://static.tacdn.com/img2/brand_refresh/Tripadvisor_lockup_horizontal_secondary_registered.svg" 
    alt="Tripadvisor" 
    className={styles.taLogo} 
  />
);

const BubbleRating = ({ rating, size = "small" }: { rating: number, size?: "small" | "large" }) => {
  const ratingStr = rating % 1 === 0 ? `${rating}.0` : `${rating}`;
  const imageUrl = `https://www.tripadvisor.com/img/cdsi/img2/ratings/traveler/${ratingStr}-66827-5.svg`;

  return (
    <img 
      src={imageUrl} 
      alt={`${rating} bubbles`} 
      className={size === "large" ? styles.largeRatingStars : styles.ratingStars} 
    />
  );
  };

  export const TripAdvisorButton: React.FC<TripAdvisorButtonProps> = ({ locationId }) => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [overallRating, setOverallRating] = useState<number | null>(null);

  const tourUrl = `https://www.tripadvisor.com/Attraction_Review-g1-d${locationId}-Reviews.html`;


  const currentReview = reviews[currentIndex];
  const reviewerName = getReviewerName(currentReview);
  const reviewerInitial = reviewerName.charAt(0).toUpperCase();

  useEffect(() => {
    const fetchUrl = `/get-tripadvisor-reviews?locationId=${encodeURIComponent(locationId)}`;

    fetch(fetchUrl)
      .then(async (res) => {
        if (!res.ok) {
          let errorMessage = "Failed to fetch reviews";

          try {
            const errorData = await res.json();
            errorMessage = errorData?.error || errorData?.message || errorMessage;
          } catch {
            // ignore JSON parse failure and keep default message
          }

          throw new Error(errorMessage);
        }

        return res.json();
      })
      .then(data => {
        const fetchedReviews = getReviewsFromResponse(data).slice(0, 5);
        setReviews(fetchedReviews);
        setOverallRating(Number(data?.rating) || null);
        setError(fetchedReviews.length === 0);
        setLoading(false);
      })
      .catch(err => {
        console.error("TripAdvisor Error:", err);
        setError(true);
        setLoading(false);
      });
  }, [locationId]);

  useEffect(() => {
    if (reviews.length > 0) {
      const timer = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % reviews.length);
      }, 5000);
      return () => clearInterval(timer);
    }
  }, [reviews.length]);

  if (loading) return (
    <div className="">
      <div className={styles.tripadvisorContent}>
        <TripAdvisorLogo />
        <div className={styles.tripadvisorRatingInfo}>
          <span className={styles.reviewCount}>Loading reviews...</span>
        </div>
      </div>
    </div>
  );

  if (error || !currentReview) {
    return (
      <div className={styles.tripAdvisorContainer}>
        <div className={styles.headerSection}>
          <div className={styles.logoAndButton}>
            <TripAdvisorLogo />
            <a
              href={tourUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.tripAdvisorButton}
            >
              <span>Review us on Tripadvisor</span>
            </a>
          </div>
          <div className={styles.ratingSummary}>
            <span className={styles.summaryText}>No reviews available</span>
          </div>
        </div>
      </div>
    );
  }

  const averageRating = Number(
    (
      reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length
    ).toFixed(1)
  );
  const displayRating = overallRating ?? averageRating;

  return (
    <div className={styles.tripAdvisorContainer}>
      <div className={styles.headerSection}>
        <div className={styles.logoAndButton}>
          <TripAdvisorLogo />
          <a 
            href={tourUrl} 
            target="_blank" 
            rel="noopener noreferrer" 
            className={styles.tripAdvisorButton}
          >
            <span>Review us on Tripadvisor</span>
          </a>
        </div>
        <div className={styles.ratingSummary}>
          <BubbleRating rating={displayRating} size="large" />
          <span className={styles.summaryText}>
            {`${displayRating} - ${getRatingLabel(displayRating)}`}
          </span>
        </div>
      </div>

      <div className={styles.reviewsCarouselSection}>
        <div className={styles.reviewSlide} key={currentIndex}>
          <div className={styles.cardHeader}>
            <BubbleRating rating={currentReview.rating} />
          </div>
          {currentReview.title && (
            <h3 className={styles.reviewTitle}>{currentReview.title}</h3>
          )}
          <p className={styles.reviewText}>"{currentReview.text}"</p>
          <div className={styles.reviewFooter}>
            <div className={styles.authorInitial}>
              {reviewerInitial}
            </div>
            <div className={styles.authorInfo}>
              <span className={styles.reviewAuthor}>{reviewerName}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};